import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Save, Sparkles, Loader2, BookOpen, RefreshCw, Presentation, ClipboardCheck, Headphones, Video, Layers, ChevronDown, Upload, X, FileText, ImageIcon, ChevronRight, Network, Gamepad2, Pencil } from "lucide-react";
import NodePodcast from "@/components/NodePodcast";
import MindMapLessonView, { type MindMapLesson } from "@/components/MindMapLessonView";
import { type SimulationConfig } from "@/components/SimulationLessonView";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCourseContext } from "@/contexts/CourseContext";

interface NodeContent {
  id?: string;
  skill_node_id: string;
  content: string;
  content_type: string;
}

interface SkillNode {
  id: string;
  title: string;
  description: string | null;
  skill_tree_id: string;
}

const ManageNodeLesson = () => {
  const { nodeId, projectId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setCourseContext, clearCourseContext } = useCourseContext();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [node, setNode] = useState<SkillNode | null>(null);
  const [content, setContent] = useState<NodeContent | null>(null);
  const [editedContent, setEditedContent] = useState("");
  const [mindMapLesson, setMindMapLesson] = useState<MindMapLesson | null>(null);
  const [simulationLesson, setSimulationLesson] = useState<SimulationConfig | null>(null);
  const [simDialogOpen, setSimDialogOpen] = useState(false);
  const [simUrl, setSimUrl] = useState("");
  const [simTitle, setSimTitle] = useState("");
  const [simInstructions, setSimInstructions] = useState("");
  const [simPassingScore, setSimPassingScore] = useState("");
  const [courseName, setCourseName] = useState<string | null>(null);
  const [contentDepth, setContentDepth] = useState<1 | 2 | 3>(2);

  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [podcastOpen, setPodcastOpen] = useState(false);
  const [contextDialogOpen, setContextDialogOpen] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [contextFiles, setContextFiles] = useState<{ name: string; content: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Set context when node loads
  useEffect(() => {
    if (node && projectId) {
      setCourseContext({
        courseId: projectId,
        courseName: courseName,
        nodeId: node.id,
        nodeName: node.title,
        pageType: "node-lesson",
      });
    }
    
    return () => {
      clearCourseContext();
    };
  }, [node, projectId, courseName]);

  useEffect(() => {
    if (nodeId) {
      fetchNodeAndContent();
    }
  }, [nodeId]);

  // Fetch course name for context
  useEffect(() => {
    const fetchCourseName = async () => {
      if (projectId) {
        const { data } = await supabase
          .from("learning_projects")
          .select("title")
          .eq("id", projectId)
          .single();
        if (data) setCourseName(data.title);
      }
    };
    fetchCourseName();
  }, [projectId]);

  const tryParseMindMap = (raw: string): MindMapLesson | null => {
    try {
      const parsed = JSON.parse(raw);
      if (parsed.centralTopic && Array.isArray(parsed.nodes)) return parsed as MindMapLesson;
    } catch { /* not JSON */ }
    return null;
  };

  const tryParseSimulation = (raw: string): SimulationConfig | null => {
    try {
      const parsed = JSON.parse(raw);
      if (parsed.type === "simulation" && typeof parsed.url === "string") return parsed as SimulationConfig;
    } catch { /* not JSON */ }
    return null;
  };

  const openSimDialog = (existing?: SimulationConfig | null) => {
    setSimUrl(existing?.url ?? "");
    setSimTitle(existing?.title ?? "");
    setSimInstructions(existing?.instructions ?? "");
    setSimPassingScore(existing?.passingScore != null ? String(existing.passingScore) : "");
    setSimDialogOpen(true);
  };

  const fetchNodeAndContent = async () => {
    setLoading(true);
    try {
      const { data: nodeData, error: nodeError } = await supabase
        .from("skill_nodes")
        .select("*")
        .eq("id", nodeId)
        .single();

      if (nodeError) throw nodeError;
      setNode(nodeData);

      const { data: contentData, error: contentError } = await supabase
        .from("skill_node_content")
        .select("*")
        .eq("skill_node_id", nodeId)
        .maybeSingle();

      if (contentError && contentError.code !== "PGRST116") throw contentError;
      
      if (contentData) {
        setContent(contentData);
        const mm = tryParseMindMap(contentData.content);
        const sim = mm ? null : tryParseSimulation(contentData.content);
        if (mm) {
          setMindMapLesson(mm);
          setSimulationLesson(null);
          setEditedContent("");
        } else if (sim) {
          setMindMapLesson(null);
          setSimulationLesson(sim);
          setEditedContent("");
        } else {
          setMindMapLesson(null);
          setSimulationLesson(null);
          setEditedContent(contentData.content);
        }
      } else {
        setMindMapLesson(null);
        setSimulationLesson(null);
        setEditedContent("");
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error loading lesson",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!nodeId || !editedContent.trim()) return;

    setSaving(true);
    try {
      if (content?.id) {
        // Update existing content
        const { error } = await supabase
          .from("skill_node_content")
          .update({ content: editedContent })
          .eq("id", content.id);
        if (error) throw error;
      } else {
        // Create new content
        const { error } = await supabase
          .from("skill_node_content")
          .insert({
            skill_node_id: nodeId,
            content: editedContent,
            content_type: "text",
          });
        if (error) throw error;
      }

      toast({
        title: "Saved!",
        description: "Lesson content has been saved successfully.",
      });
      
      fetchNodeAndContent();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error saving",
        description: error.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateContent = async (withContext = false, enableImages = false) => {
    if (!nodeId) return;

    const body: Record<string, unknown> = { skillNodeId: nodeId, contentDepth };
    if (enableImages) body.enableImages = true;
    if (withContext) {
      if (customPrompt.trim()) body.customPrompt = customPrompt.trim();
      if (contextFiles.length > 0) body.contextFiles = contextFiles;
    }

    setContextDialogOpen(false);
    setGenerating(true);
    try {
      const { data, error } = await invokeFunction("generate-node-content", { body });

      if (error) throw error;

      toast({
        title: "Content generated!",
        description: "AI has created lesson content for this node.",
      });
      
      fetchNodeAndContent();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error generating content",
        description: error.message,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateMindMap = async () => {
    if (!nodeId) return;
    setGenerating(true);
    try {
      const { data, error } = await invokeFunction("generate-mindmap-lesson", {
        body: { skillNodeId: nodeId, action: "generate" },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      // Set the mind map directly from the response
      if (data?.lesson) {
        setMindMapLesson(data.lesson as MindMapLesson);
        setContent((prev) => prev
          ? { ...prev, content: JSON.stringify(data.lesson), content_type: "mindmap" }
          : { skill_node_id: nodeId, content: JSON.stringify(data.lesson), content_type: "mindmap" },
        );
      }

      toast({
        title: "Mind map lesson generated!",
        description: "Click sections to expand them with detailed content.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error generating mind map",
        description: error.message,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveSimulation = async () => {
    if (!nodeId || !simUrl.trim()) return;

    try {
      new URL(simUrl.trim());
    } catch {
      toast({ variant: "destructive", title: "Invalid URL", description: "Enter a full URL, e.g. https://example.com" });
      return;
    }

    const payload: SimulationConfig = {
      type: "simulation",
      url: simUrl.trim(),
      ...(simTitle.trim() && { title: simTitle.trim() }),
      ...(simInstructions.trim() && { instructions: simInstructions.trim() }),
      ...(simPassingScore.trim() && { passingScore: Number(simPassingScore.trim()) }),
    };

    setSaving(true);
    try {
      const serialized = JSON.stringify(payload);
      if (content?.id) {
        const { error } = await supabase
          .from("skill_node_content")
          .update({ content: serialized, content_type: "simulation" })
          .eq("id", content.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("skill_node_content")
          .insert({ skill_node_id: nodeId, content: serialized, content_type: "simulation" });
        if (error) throw error;
      }

      setSimDialogOpen(false);
      toast({ title: "Simulation embedded!", description: "Learners will see it in place of the lesson text." });
      fetchNodeAndContent();
    } catch (error: any) {
      toast({ variant: "destructive", title: "Error saving simulation", description: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleContextFilesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles: { name: string; content: string }[] = [];
    for (const file of Array.from(files)) {
      if (file.size > 2 * 1024 * 1024) {
        toast({ variant: "destructive", title: "File too large", description: `${file.name} exceeds 2 MB limit.` });
        continue;
      }
      const text = await file.text();
      newFiles.push({ name: file.name, content: text.substring(0, 50000) });
    }
    setContextFiles((prev) => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeContextFile = (index: number) => {
    setContextFiles((prev) => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b sticky top-0 bg-background z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/project/${projectId}`, { state: { openMaterials: true } })}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-primary" />
                {node?.title ?? "Manage Lesson"}
              </h1>
              {node?.description && (
                <p className="text-sm text-muted-foreground mt-0.5 max-w-xl">{node.description}</p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={String(contentDepth)}
              onValueChange={(v) => setContentDepth(Number(v) as 1 | 2 | 3)}
              disabled={generating}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Content depth" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Level 1: Basic</SelectItem>
                <SelectItem value="2">Level 2: Standard</SelectItem>
                <SelectItem value="3">Level 3: In-depth</SelectItem>
              </SelectContent>
            </Select>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={generating}>
                  {generating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="mr-2 h-4 w-4" />
                  )}
                  {generating ? "Generating..." : "AI Generate"}
                  <ChevronDown className="ml-1.5 h-3.5 w-3.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleGenerateContent(false)}>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setContextDialogOpen(true)}>
                  <FileText className="mr-2 h-4 w-4" />
                  Generate with Context
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleGenerateContent(false, true)}>
                  <ImageIcon className="mr-2 h-4 w-4" />
                  Generate with Images (Beta)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleGenerateMindMap}>
                  <Network className="mr-2 h-4 w-4" />
                  Generate Mind Map Lesson
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" onClick={() => openSimDialog(simulationLesson)} disabled={saving}>
              <Gamepad2 className="mr-2 h-4 w-4" />
              Embed Simulation
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="grid gap-6">
          {/* Learning modalities: slides, quiz, flashcards, podcast, video */}
          <Card>
            <CardHeader>
              <CardTitle>Learning modalities</CardTitle>
              <CardDescription>
                Generate or manage different content formats for this node.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                <div className="flex items-center gap-3 rounded-lg border p-3 bg-muted/30">
                  <BookOpen className="h-8 w-8 shrink-0 text-primary" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">Lesson</p>
                    <p className="text-xs text-muted-foreground">You are here — edit text and generate with AI</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="h-auto justify-start gap-3 px-3 py-3"
                  onClick={() => navigate(`/project/${projectId}/node/${nodeId}/slides`)}
                >
                  <Presentation className="h-8 w-8 shrink-0" />
                  <div className="min-w-0 flex-1 text-left">
                    <p className="font-medium">Slides</p>
                    <p className="text-xs text-muted-foreground">Generate and edit presentation decks</p>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto justify-start gap-3 px-3 py-3"
                  onClick={() => navigate(`/project/${projectId}/node/${nodeId}/exam`)}
                >
                  <ClipboardCheck className="h-8 w-8 shrink-0" />
                  <div className="min-w-0 flex-1 text-left">
                    <p className="font-medium">Quiz</p>
                    <p className="text-xs text-muted-foreground">Create and manage exam questions</p>
                  </div>
                </Button>
                <div className="flex items-center gap-3 rounded-lg border border-dashed p-3 opacity-75">
                  <Layers className="h-8 w-8 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">Flashcards</p>
                    <p className="text-xs text-muted-foreground">Coming soon</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="h-auto justify-start gap-3 px-3 py-3"
                  onClick={() => setPodcastOpen((v) => !v)}
                >
                  <Headphones className="h-8 w-8 shrink-0" />
                  <div className="min-w-0 flex-1 text-left">
                    <p className="font-medium">Podcast</p>
                    <p className="text-xs text-muted-foreground">AI two-host audio overview</p>
                  </div>
                  <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${podcastOpen ? "rotate-90" : ""}`} />
                </Button>
                <Button
                  variant="outline"
                  className="h-auto justify-start gap-3 px-3 py-3"
                  onClick={() => openSimDialog(simulationLesson)}
                >
                  <Gamepad2 className="h-8 w-8 shrink-0" />
                  <div className="min-w-0 flex-1 text-left">
                    <p className="font-medium">Simulation</p>
                    <p className="text-xs text-muted-foreground">Embed an interactive simulation</p>
                  </div>
                </Button>
                <div className="flex items-center gap-3 rounded-lg border border-dashed p-3 opacity-75">
                  <Video className="h-8 w-8 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">Video</p>
                    <p className="text-xs text-muted-foreground">Coming soon</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Node Info */}
          <Card>
            <CardHeader>
              <CardTitle>{node?.title}</CardTitle>
              <CardDescription>
                {node?.description ? (
                  node.description
                ) : (
                  <span className="flex items-center gap-2 flex-wrap">
                    <span className="text-muted-foreground/60 italic">No description</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      disabled={generatingDescription}
                      onClick={async () => {
                        if (!node || !projectId) return;
                        setGeneratingDescription(true);
                        try {
                          const { data: { session } } = await supabase.auth.getSession();
                          const { data, error } = await invokeFunction("course-assistant", {
                            body: {
                              messages: [],
                              mode: "describe",
                              context: {
                                nodeId: node.id,
                                nodeName: node.title,
                                courseId: projectId,
                                courseName: courseName,
                                pageType: "node-lesson",
                              },
                              _authToken: session?.access_token,
                            },
                          });
                          if (error || !data?.description) throw new Error("Failed to generate description");
                          setNode((prev) => prev ? { ...prev, description: data.description } : prev);
                          toast({ title: "Description generated" });
                        } catch {
                          toast({ variant: "destructive", title: "Failed to generate description" });
                        } finally {
                          setGeneratingDescription(false);
                        }
                      }}
                    >
                      {generatingDescription ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                      {generatingDescription ? "Generating…" : "Generate"}
                    </Button>
                  </span>
                )}
              </CardDescription>
            </CardHeader>
          </Card>

          {/* Mind Map View — takes over as the primary content interface */}
          {mindMapLesson ? (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Network className="h-4 w-4" />
                  <span>Mind map lesson — click sections below to expand</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={fetchNodeAndContent}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setMindMapLesson(null);
                      setEditedContent(content?.content || "");
                    }}
                  >
                    Switch to text editor
                  </Button>
                </div>
              </div>
              <MindMapLessonView
                lesson={mindMapLesson}
                skillNodeId={nodeId!}
                onLessonUpdated={(updated) => {
                  setMindMapLesson(updated);
                  setContent((prev) =>
                    prev ? { ...prev, content: JSON.stringify(updated) } : prev,
                  );
                }}
              />
            </>
          ) : simulationLesson ? (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Gamepad2 className="h-4 w-4" />
                  <span>Embedded simulation lesson</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={fetchNodeAndContent}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSimulationLesson(null);
                      setEditedContent(content?.content || "");
                    }}
                  >
                    Switch to text editor
                  </Button>
                </div>
              </div>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{simulationLesson.title || "Simulation"}</span>
                    <Button variant="outline" size="sm" onClick={() => openSimDialog(simulationLesson)}>
                      <Pencil className="mr-2 h-3.5 w-3.5" />
                      Edit
                    </Button>
                  </CardTitle>
                  <CardDescription>{simulationLesson.url}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {simulationLesson.instructions && (
                    <p className="text-foreground/90">{simulationLesson.instructions}</p>
                  )}
                  {simulationLesson.passingScore != null && (
                    <p className="text-muted-foreground">Passing score: {simulationLesson.passingScore}/100</p>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* AI Generation Tip */}
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Sparkles className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">AI-Powered Content Generation</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Click "AI Generate" to automatically create lesson content based on 
                        the node's title, description, and course materials. You can edit the 
                        generated content to customize it for your needs.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Lesson Editor */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Lesson Content</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={fetchNodeAndContent}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </CardTitle>
                  <CardDescription>
                    Write your lesson content using Markdown formatting
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="edit" className="w-full">
                    <TabsList className="mb-4">
                      <TabsTrigger value="edit">Edit</TabsTrigger>
                      <TabsTrigger value="preview">Preview</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="edit">
                      <div className="space-y-2">
                        <Label htmlFor="content">Content (Markdown supported)</Label>
                        <Textarea
                          id="content"
                          value={editedContent}
                          onChange={(e) => setEditedContent(e.target.value)}
                          placeholder="Write your lesson content here... 

# Heading 1
## Heading 2

**Bold text** and *italic text*

- Bullet point 1
- Bullet point 2

1. Numbered item 1
2. Numbered item 2

`inline code`

```
code block
```"
                          rows={25}
                          className="font-mono text-sm"
                        />
                        <p className="text-xs text-muted-foreground">
                          Tip: Use Markdown for formatting. Headings (#), bold (**text**), 
                          italic (*text*), lists (- or 1.), code (`code`), etc.
                        </p>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="preview">
                      <div className="border rounded-lg p-6 min-h-[400px] bg-muted/30">
                        {editedContent ? (
                          <div className="prose prose-slate dark:prose-invert max-w-none prose-sm prose-headings:font-semibold prose-p:leading-relaxed prose-img:rounded-lg prose-img:shadow-md prose-img:my-4 [&_table]:w-full [&_table]:border-collapse [&_table]:my-4 [&_table]:rounded-lg [&_table]:overflow-hidden [&_table]:border [&_table]:border-border [&_th]:px-4 [&_th]:py-3 [&_th]:text-left [&_th]:font-semibold [&_th]:bg-muted [&_th]:border-b-2 [&_th]:border-border [&_td]:px-4 [&_td]:py-3 [&_td]:border-b [&_td]:border-border [&_tr:last-child_td]:border-b-0">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{editedContent}</ReactMarkdown>
                          </div>
                        ) : (
                          <p className="text-muted-foreground italic">
                            No content to preview. Start writing in the Edit tab or use AI Generate.
                          </p>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </>
          )}
          {/* Podcast panel */}
          {podcastOpen && nodeId && projectId && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Headphones className="h-5 w-5" />
                  Podcast Overview
                </CardTitle>
                <CardDescription>
                  An AI-generated two-host conversation about this lesson. Choose a TTS provider and click Generate.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <NodePodcast nodeId={nodeId} courseId={projectId} />
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Generate with Context dialog */}
      <Dialog open={contextDialogOpen} onOpenChange={setContextDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate with Context</DialogTitle>
            <DialogDescription>
              Provide additional instructions and upload reference files to guide the AI when generating this lesson.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="custom-prompt">Instructions (optional)</Label>
              <Textarea
                id="custom-prompt"
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="e.g. Focus on practical examples, use a conversational tone, include code snippets for Python..."
                rows={4}
              />
            </div>
            <div className="space-y-2">
              <Label>Reference files (optional)</Label>
              <p className="text-xs text-muted-foreground">
                Upload text files (.txt, .md, .csv, .json, etc.) to use as additional context. Max 2 MB each.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".txt,.md,.csv,.json,.xml,.html,.py,.js,.ts,.jsx,.tsx,.yaml,.yml,.log,.rtf"
                onChange={handleContextFilesChange}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="mr-2 h-3.5 w-3.5" />
                Upload Files
              </Button>
              {contextFiles.length > 0 && (
                <ul className="space-y-1.5 pt-1">
                  {contextFiles.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm bg-muted/50 rounded-md px-2.5 py-1.5">
                      <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate flex-1">{f.name}</span>
                      <button
                        type="button"
                        onClick={() => removeContextFile(i)}
                        className="shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setContextDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleGenerateContent(true)}>
              <Sparkles className="mr-2 h-4 w-4" />
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embed Simulation dialog */}
      <Dialog open={simDialogOpen} onOpenChange={setSimDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Embed Simulation</DialogTitle>
            <DialogDescription>
              Replace this lesson's content with an embedded interactive simulation. Learners see it
              full-width in place of the usual text.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="sim-url">Simulation URL</Label>
              <Input
                id="sim-url"
                value={simUrl}
                onChange={(e) => setSimUrl(e.target.value)}
                placeholder="https://your-simulation.vercel.app"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sim-title">Title (optional)</Label>
              <Input
                id="sim-title"
                value={simTitle}
                onChange={(e) => setSimTitle(e.target.value)}
                placeholder="e.g. Call for Fire Trainer"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sim-instructions">Instructions (optional)</Label>
              <Textarea
                id="sim-instructions"
                value={simInstructions}
                onChange={(e) => setSimInstructions(e.target.value)}
                placeholder="What should the learner do in this simulation?"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sim-passing-score">Passing score to unlock "Mark complete" (optional)</Label>
              <Input
                id="sim-passing-score"
                type="number"
                min={0}
                max={100}
                value={simPassingScore}
                onChange={(e) => setSimPassingScore(e.target.value)}
                placeholder="e.g. 70"
                className="max-w-[140px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSimDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSimulation} disabled={saving || !simUrl.trim()}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Gamepad2 className="mr-2 h-4 w-4" />}
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageNodeLesson;

